import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { endpoints } from '../../../utils/endpoints';
import tPaths from '../../../utils/tPaths';
import useApi from '../../../utils/useApi';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import InputForm from '../../../components/InputForm/InputForm';
import SwitchInput from '../../../components/SwitchInput/SwitchInput';
import ImportImage from '../../../components/ImportImage/ImportImage';
import useSnackbar from '../../../utils/useSnackbar';
import { ETables } from '../../../types/enums/ETables';
import { ITableData } from '../../../types/interfaces/ITableData';
import { NSPackages } from '../../../types/interfaces/NSPackages';

enum EFields {
  IMAGE = 'image',
  NAME = 'name',
  ADD_CRUISE_LINE = 'addCruiseLine',
  CRUISE_LINE_IDS = 'cruiseLineIds',
  PACKAGE_IDS = 'packageIds',
}

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const initState = {
  [EFields.IMAGE]: null as string | null,
  [EFields.NAME]: '',
  [EFields.ADD_CRUISE_LINE]: false,
  [EFields.CRUISE_LINE_IDS]: [] as string[],
  [EFields.PACKAGE_IDS]: [] as string[],
};

const AddShipDialog: React.FC<IProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { post } = useApi(null);
  const [state, setState] = useState(initState);

  const cruiseLinesEndpoint = isOpen ? endpoints.table({ tableName: ETables.CruiseLines, page: 1 }) : null;
  const packagesEndpoint = isOpen ? endpoints.table({ tableName: ETables.Packages, page: 1 }) : null;

  const { data: cruiseLinesData } = useApi<ITableData>(cruiseLinesEndpoint);
  const { data: packagesData } = useApi<ITableData>(packagesEndpoint);

  const cruiseLines = cruiseLinesData?.table || [];
  const packagesList = (packagesData?.table || []) as NSPackages.ITableItem[];

  useEffect(() => {
    if (isOpen) {
      setState(initState);
    }
  }, [isOpen]);

  const onChange = useCallback((fieldName: string, value: string | boolean | string[] | null) => {
    setState((s) => ({ ...s, [fieldName]: value }));
  }, []);

  const cruiseLineOptions = useMemo(
    () =>
      cruiseLines.map((line: any) => ({
        value: line._id || line.id,
        label: line.name,
      })),
    [cruiseLines],
  );

  const packageOptions = useMemo(
    () =>
      packagesList?.map((item) => ({
        value: item._id,
        label: `${item.title} (${item.label})`,
      })) || [],
    [packagesList],
  );

  const fields: Record<EFields, IEditForm> = useMemo(
    () => ({
      [EFields.IMAGE]: {
        fieldName: EFields.IMAGE,
        type: 'image',
        label: t(tPaths.editFormLabels.image),
        value: state[EFields.IMAGE],
        onChange,
      },
      [EFields.NAME]: {
        fieldName: EFields.NAME,
        type: 'input',
        label: t(tPaths.editFormLabels.shipName),
        value: state[EFields.NAME],
        onChange,
        fullWidth: true,
      },
      [EFields.ADD_CRUISE_LINE]: {
        fieldName: EFields.ADD_CRUISE_LINE,
        type: 'switch',
        label: t(tPaths.editFormLabels.addCruiseLine),
        value: state[EFields.ADD_CRUISE_LINE],
        onChange,
      },
      [EFields.CRUISE_LINE_IDS]: {
        fieldName: EFields.CRUISE_LINE_IDS,
        type: 'select',
        label: t(tPaths.editFormLabels.cruiseLine),
        value: state[EFields.CRUISE_LINE_IDS],
        onChange,
        variants: cruiseLineOptions,
        multiple: true,
        fullWidth: true,
        hidden: !state[EFields.ADD_CRUISE_LINE],
      },
      [EFields.PACKAGE_IDS]: {
        fieldName: EFields.PACKAGE_IDS,
        type: 'select',
        label: t(tPaths.editFormLabels.plans),
        value: state[EFields.PACKAGE_IDS],
        onChange,
        variants: packageOptions,
        multiple: true,
        fullWidth: true,
      },
    }),
    [cruiseLineOptions, onChange, packageOptions, state, t],
  );

  const handleSubmit = useCallback(() => {
    if (!state[EFields.NAME].trim()) {
      snackbar({ severity: 'error', message: t(tPaths.global.wentWrong) });
      return;
    }

    post({
      endpoint: endpoints.cruiseShips,
      body: {
        name: { en: state[EFields.NAME].trim() },
        image: state[EFields.IMAGE] ? { en: state[EFields.IMAGE] } : {},
        cruiseLineIds: state[EFields.ADD_CRUISE_LINE] ? state[EFields.CRUISE_LINE_IDS] : [],
        packageIds: state[EFields.PACKAGE_IDS],
        status: 'Active',
      },
      onSuccess: () => {
        snackbar({ severity: 'success', message: t(tPaths.itemContent.successOnAdd) });
        onSuccess();
        onClose();
      },
    });
  }, [onClose, onSuccess, post, snackbar, state, t]);

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Typography variant="h6">{t(tPaths.itemContent.addCruiseShip)}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box pt={1}>
          <ImportImage {...fields[EFields.IMAGE]} />
        </Box>
        <Box pt={3}>
          <InputForm {...fields[EFields.NAME]} />
        </Box>
        <Box pt={3}>
          <SwitchInput {...fields[EFields.ADD_CRUISE_LINE]} />
        </Box>
        {!fields[EFields.CRUISE_LINE_IDS].hidden && (
          <Box pt={3}>
            <CustomSelect {...fields[EFields.CRUISE_LINE_IDS]} />
          </Box>
        )}
        <Box pt={3}>
          <CustomSelect {...fields[EFields.PACKAGE_IDS]} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(tPaths.global.cancel)}</Button>
        <Button color="primary" onClick={handleSubmit}>
          {t(tPaths.itemContent.addCruiseShip)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddShipDialog;
